import { useState } from "react";
import { Alert, Button, ButtonToolbar, Card, Form } from "react-bootstrap";
import { useHistory } from "react-router-dom";

import { Loader } from "@launchmetrics/lm-ui";
import Breadcrumb from 'components/LM/common/Breadcrumb';
import Icons from "components/LM/common/VoiceRelationsIcons";
import K2 from 'lib/ajax/K2Api';

const MAX_ALLOWED_RECOVERIES = 100;

function Recovery({ location }) {
	const history = useHistory();
	const [processing, setProcessing] = useState(false);
	const [error, setError] = useState(null);

	const { query, types } = location.state || { query: "", types: { facebook: false, instagram: false, youtube: false } };

	const onChange = (ev) => {
		setError(null);
		if (ev.target.type === "textarea") {
			history.replace(location.pathname, { query: ev.target.value, types });
		} else if (ev.target.type === "checkbox") {
			types[ev.target.value] = ev.target.checked;
			history.replace(location.pathname, { query: query, types });
		}

	};

	const onSubmit = async (ev) => {
		ev.preventDefault();
		const voiceIds = getIdsFromQuery(query);
		const profileTypes = Object.keys(types).filter(type => types[type]);

		if (voiceIds.length === 0 || profileTypes.length === 0) {
			setError("No voice ids or social media types selected");
			return;
		}

		setProcessing(true);
		const results = await submitMassRecovery(voiceIds, profileTypes);
		history.push(`${location.pathname}/results`, { results, types: profileTypes });
	};

	return <RecoveryForm
		query={query}
		error={error}
		types={types}
		processing={processing}
		onSubmit={onSubmit}
		onChange={onChange}
	/>
}

function RecoveryForm(props) {
	let { query, error, types } = props;
	const { processing, onSubmit, onChange } = props;

	const isLimitReached = getIdsFromQuery(query).length > MAX_ALLOWED_RECOVERIES;

	return (
		<div className="voices-mass-recovery-form">
			{error && <Alert variant="danger"><p className='m-2'>{error}</p></Alert>}
			<Form>
				<Breadcrumb items={['Voices', 'Mass Recovery']} />
				<Card>
					<Card.Header>
						<Card.Title tag="h5">
							Paste here all voice ids (max. {MAX_ALLOWED_RECOVERIES})
						</Card.Title>
						<h6 className="card-subtitle text-muted">
							Remember, one id per line and don't forget to select at least one socialmedia network
						</h6>
					</Card.Header>
					<Card.Body>
						<Form.Group className="mb-3" controlId="in">
							<Form.Control
								as="textarea"
								rows="10"
								disabled={processing}
								onChange={onChange}
								value={query}
							/>

							<TypeCheckbox type="facebook" onChange={onChange} checked={types.facebook} />
							<TypeCheckbox type="instagram" onChange={onChange} checked={types.instagram} />
							<TypeCheckbox type="youtube" onChange={onChange} checked={types.youtube} />

						</Form.Group>

						<ButtonToolbar>
							<Button
								color="info"
								type="submit"
								className="mr-1 mb-1"
								disabled={processing || isLimitReached || !query || !query.length}
								onClick={onSubmit}
							>
								Submit
								{processing && <Loader styleProps={{ marginLeft: '10px'}}size={20} />}
							</Button>
							{isLimitReached ? <span className="text-danger">⚠️ Limit reached</span> : null}
						</ButtonToolbar>
					</Card.Body>
				</Card>
			</Form>
		</div>
	)
}

function TypeCheckbox({ type, checked, onChange }) {
	return (
		<Form.Check inline
			name="type"
			type="checkbox"
			checked={checked}
			value={type}
			label={<><Icons size="16" type={type} /><span className="text-capitalize">{type}</span></>}
			onChange={onChange} />
	);
}

function getIdsFromQuery(query) {
	const ids = query.split(/\n+/).filter(id => id.length > 0);
	return [...new Set(ids)]; // Using set a dereferencing will act removing duplicates from the array
}

async function submitMassRecovery(voiceIds, profileTypes) {
	const voices = await retrieveVoices(voiceIds);
	const recoveries = await submitRecoveries(Object.values(voices), profileTypes);
	return voiceIds.map(id => recoveries[id] || { id, error: "Voice not found" });
}

async function retrieveVoices(voiceIds) {
	const k2 = new K2();

	const voiceRequests = [];
	const voices = {};
	voiceIds.forEach(id => {
		const request = k2.get(`/voices/${id}`)
			.then(res => voices[id] = res.data);
		voiceRequests.push(request);
	});

	await Promise.allSettled(voiceRequests);
	return voices;
}

async function submitRecoveries(voices, profileTypes) {
	const k2 = new K2();

	const beginDate = new Date();
	beginDate.setUTCMonth(beginDate.getMonth() - 13);
	beginDate.setUTCDate(1);
	beginDate.setUTCHours(0, 0, 0);
	const endDate = new Date();

	const recoveryRequests = [];
	const recoveries = {};

	voices.forEach(voice => {
		recoveries[voice.id] = {
			id: voice.id,
			name: voice.name
		};

		profileTypes.forEach(type => {
			const profiles = voice.socialmedia[type];
			recoveries[voice.id][type] = [];
			if (profiles.length === 0) {
				return;
			}

			profiles.forEach(profile => {
				const request = k2.post(`/socialmedia/${type}/recovery`, { id: profile.id, begin_date: beginDate, end_date: endDate });

				const recoveryProfile = {
					id: profile.id,
					name: profile.name,
				};

				request.then(() => {
					recoveries[voice.id][type].push({ success: true, ...recoveryProfile });
				}).catch(err => {
					recoveries[voice.id][type].push({ success: false, message: err.response.data.errors[0], ...recoveryProfile });
				});

				recoveryRequests.push(request);

			});
		});
	});

	await Promise.allSettled(recoveryRequests);
	return recoveries;
}

export default Recovery;
